import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { EQM1010_R01Dto } from './Dto_EQM1010_R01';

export interface EQM1010_R01Filter {
    compCd: string;
    plcaddressid: number | null;
    baseDate: Date | undefined;
}

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_EQM1010_R01 {
    EQM1010_R01Filter: EQM1010_R01Filter;
    EQM1010_R01PastFilter: EQM1010_R01Filter;
    setEQM1010_R01Filter: (filter: Partial<EQM1010_R01Filter>) => void;
    setEQM1010_R01PastFilter: (filter: Partial<EQM1010_R01Filter>) => void;

    // === 리스트 관련 ===
    EQM1010_R01List: EQM1010_R01Dto[];
    setEQM1010_R01List: (list: EQM1010_R01Dto[]) => void;
    insertEQM1010_R01List: (data: EQM1010_R01Dto[], index?: number) => void;
    removeEQM1010_R01List: (index: number) => void;
    updateEQM1010_R01List: (data: EQM1010_R01Dto, index: number) => void;
    
    // === 선택 관련 ===
    selectedEQM1010_R01List: string[];
    setSelectedEQM1010_R01List: (idList: string[]) => void;
    addSelectedEQM1010_R01List: (idList: string[]) => void;
    removeSelectedEQM1010_R01List: (idList?: string[]) => void;
    initSelectedEQM1010_R01List: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

    scrollTop: number | null;
    setScrollTop: (top: number | null) => void;

    latestDataList: EQM1010_R01Dto[] | null;
    setLatestDataList: (data: EQM1010_R01Dto[] | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    // === 전체 초기화 ===
    initData: () => void;

}

export const defaultEQM1010_R01Filter: EQM1010_R01Filter = {
    compCd: '',
    plcaddressid: null,
    baseDate: new Date(),
};

/**
 * Zustand 스토어 정의
 */
export const useEQM1010_R01Store = create<Store_EQM1010_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_EQM1010_R01, 'EQM1010_R01Filter' | 'EQM1010_R01PastFilter'>) => 
        (filter: Partial<EQM1010_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /**
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        EQM1010_R01Filter: defaultEQM1010_R01Filter,
        EQM1010_R01PastFilter: defaultEQM1010_R01Filter,
        setEQM1010_R01Filter: createFilterSetter('EQM1010_R01Filter'),
        setEQM1010_R01PastFilter: createFilterSetter('EQM1010_R01PastFilter'),

        // === 리스트 관련 ===
        EQM1010_R01List: [],
        setEQM1010_R01List: (list: EQM1010_R01Dto[]) => set({ EQM1010_R01List: list }),
        insertEQM1010_R01List: (data: EQM1010_R01Dto[], index?: number) => set((state) => {
            const _list = [...state.EQM1010_R01List];
            
            if (index !== undefined) {
                _list.splice(index, 0, ...data);
            } else {
                _list.push(...data);
            }
            return { ...state, EQM1010_R01List: _list };
        }),
        removeEQM1010_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            EQM1010_R01List: state.EQM1010_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updateEQM1010_R01List: (data: EQM1010_R01Dto, index: number) => set((state) => {
            const _list = [...state.EQM1010_R01List];
            _list[index] = data;
            return { ...state, EQM1010_R01List: _list };
        }),

        // === 선택 관련 ===
        selectedEQM1010_R01List: [],
        setSelectedEQM1010_R01List: (idList: string[]) => set({ selectedEQM1010_R01List: idList }),
        addSelectedEQM1010_R01List: (idList: string[]) => set((state) => {
            const _selectedEQM1010_R01List = [...state.selectedEQM1010_R01List];
            _selectedEQM1010_R01List.push(...idList.filter(value => !_selectedEQM1010_R01List.includes(value)));
            return { ...state, selectedEQM1010_R01List: _selectedEQM1010_R01List };
        }),
        removeSelectedEQM1010_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedEQM1010_R01List: [] };
            }
            
            return {
                ...state,
                selectedEQM1010_R01List: removeItemsFromArray(state.selectedEQM1010_R01List, idList),
            };
        }),
        initSelectedEQM1010_R01List: () => set({ selectedEQM1010_R01List: [] }),
        
        latestDataList: null,
        setLatestDataList: (data) => set({ latestDataList: data }),


        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),

        scrollTop: null,
        setScrollTop: (top: number | null) => set({ scrollTop: top }),

        allCount: 0,
        setAllCount: (allCount: number) => set({ allCount }),

        // === 전체 초기화 ===
        initData: () => set(() => ({
            EQM1010_R01Filter: defaultEQM1010_R01Filter,
            EQM1010_R01PastFilter: defaultEQM1010_R01Filter,
            EQM1010_R01List: [],
            selectedEQM1010_R01List: [],
            columnState: null,
            allCount: 0,
            latestDataList: null,
            scrollTop: null,
        })),
    };
});


